import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { QueryKeys, client } from "queryClient";
import { CalcTask } from "types";
import { getRunningOrPendingCalcTaskList } from "modules/calctasks/api";

/**
 * Laufende und ausstehende Preisberechnungen, live über AppSync-Subscriptions.
 *
 * Zwei bewusste Abweichungen von useSyncTaskRunningList / useStockTaskRunningList:
 *
 * 1. Eigener Query-Key-Namensraum (QueryKeys.RunningTasks) statt
 *    [QueryKeys.CalcTasks, "running"]. Sonst invalidiert useCalcTaskCreate diese
 *    Liste per Präfix-Match mit, lädt über einen eventual-consistenten GSI neu und
 *    verliert dabei möglicherweise die gerade erzeugte Pending-Zeile.
 *
 * 2. Kurze staleTime statt 15 Minuten. Die Subscriptions leben nur, solange der
 *    Hook gemountet ist; nach einer Navigation wäre ein 15 Minuten alter Snapshot
 *    die Grundlage der Sperrlogik und würde die Oberfläche blockieren, obwohl
 *    längst nichts mehr läuft.
 */
export const useCalcTaskRunningList = () => {
  const [calcTaskList, setCalcTaskList] = useState<CalcTask[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: [QueryKeys.RunningTasks, "calc"],
    queryFn: async () => {
      const calcTaskList = await getRunningOrPendingCalcTaskList();

      return calcTaskList;
    },
    staleTime: 1000 * 15,
    gcTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (data) {
      setCalcTaskList(data);
    }
  }, [data]);

  useEffect(() => {
    const createSub = client.models.CalcTask.onCreate().subscribe({
      next: (created) => {
        // Dedupe über die ID: unter StrictMode wird der Effect doppelt gemountet,
        // und ein Event kann mehrfach eintreffen. Ohne Dedupe stünde dieselbe
        // Aufgabe zweimal in der Liste.
        setCalcTaskList((prev) =>
          prev.some((item) => item.id === created.id)
            ? prev
            : [created, ...prev],
        );
      },
      error: (error) => console.warn(error),
    });

    const updateSub = client.models.CalcTask.onUpdate().subscribe({
      next: (updated) => {
        setCalcTaskList((prev) => {
          const isKnown = prev.some((item) => item.id === updated.id);

          // Eine Zeile, die von Pending/Running auf einen Terminalstatus wechselt,
          // muss trotzdem ankommen - sonst bliebe sie ewig als "laufend" stehen.
          if (!isKnown) {
            return updated.status === "Running" || updated.status === "Pending"
              ? [updated, ...prev]
              : prev;
          }

          return prev.map((item) => (item.id === updated.id ? updated : item));
        });
      },
      error: (error) => console.warn(error),
    });

    const deleteSub = client.models.CalcTask.onDelete().subscribe({
      next: (deleted) => {
        setCalcTaskList((prev) =>
          prev.filter((item) => item.id !== deleted.id),
        );
      },
      error: (error) => console.warn(error),
    });

    return () => {
      createSub.unsubscribe();
      updateSub.unsubscribe();
      deleteSub.unsubscribe();
    };
  }, []);

  return {
    calcTaskList,
    isLoading,
  };
};
